/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import { useEffect, useState } from "react";
import { setConfirmDialog, setIsLoadingStatus, setOpenToast,setErrorMsg } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { useCreateUserMutation, useDeleteUserMutation, useGetUserQuery, useUpdateUserMutation } from "../../../../store/api/admin/adminApiSlice";
import { IUser } from "../../../../store/api/admin/types";
import dayjs, { Dayjs } from "dayjs";
import { errorMessage, getFutureDate, titleCase } from "../../../../utils/helpers";
import { resetUser, userPayload } from "../../../helpers";
import { isCreateUserSelector } from "../../../../store/api/admin/selectors";
import { useSelector } from "react-redux";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import { isSpecialChars, isValidEmail, isValidPassword, validatePhoneNumber } from "../../../../utils/validations";
import { setIsCreateUser } from "../../../../store/api/admin/reducers";
import { useProfileDisplay } from "../Profiles/hooks";
import { isLoadingStatusSelector } from "../../../../store/helpers/helperSelectors";
import { IKeyValue } from "../../types";
import { useNavigate } from "react-router-dom";
import { selectProfilePermissionsSelector } from "../../../../store/api/auth/selectors";
import ProfilePermissions from "../../../../utils/ProfilePermissions";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { usernameSelector } from "../../../../store/api/auth/selectors";
import { logout, } from "../../../../store/api/auth/reducer";

export const useCreateUserForm = () => {
  const dispatch = useStoreDispatch();
  const navigate = useNavigate();

  const [createUser, createUserStatus] = useCreateUserMutation();
  const [updateUser, updateUserStatus] = useUpdateUserMutation();
  const [deleteUser, deleteUserStatus] = useDeleteUserMutation();
  const { data = [], isLoading, refetch } = useGetUserQuery();
  const profiles = useProfileDisplay();
  const [errMsg, setErrMsg] = useState<IKeyValue>({});

  const [userFormData, setUserFormData] = useState<IUser>(resetUser);
  const [passExpireDate, setPassExpireDate] = useState<Dayjs | null>(dayjs(getFutureDate(30)));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [passNeverExpire, setPassNeverExpire] = useState(false);
  const [isChangePass, setIsChangePass] = useState(false);

  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const { isCreateUser } = useSelector(isCreateUserSelector);
  const isLoadingStatus = useSelector(isLoadingStatusSelector);
  const loggedInUserName = useSelector(usernameSelector);

  const username = "username";
  const password = "password";
  const confirmPassword = "confirmPassword";
  const passwordExpiryDate = "passwordExpiryDate";
  const firstName = "firstName";
  const lastName = "lastName";
  const department = "department";
  const location = "location";
  const phone = "phone";
  const email = "email";

  const errorMsg = useSelector(errorMsgSelector)
  const clearInputList ={"username":"","password":"","confirmPassword":"","passwordExpiryDate":"","firstName":"","lastName":"","department":"","location":"","phone":"","email":""}


  useEffect(() => {
    dispatch(setIsLoadingStatus({ user: createUserStatus.isLoading || updateUserStatus.isLoading || deleteUserStatus.isLoading }));
  }, [createUserStatus.isLoading, deleteUserStatus.isLoading, dispatch, updateUserStatus.isLoading]);
  
  useEffect(() => { 
     dispatch(setErrorMsg({...errorMsg, ...clearInputList })); 
  },[]);

  useEffect(() => {  
    if (Object.keys(data).length > 0) {     
      setErrMsg({});
      setSelectedIndex(0);
      setUserFormData({ ...data[0], profileId: data[0]?.profile?.id || 0 });   
      if (!!data[0]?.passExpireDate) {
        setPassExpireDate(dayjs(data[0]?.passExpireDate));
      }
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (isCreateUser) setPassNeverExpire(true);
    else setErrMsg({});
  }, [isCreateUser]);

  const submitHandler = async (): Promise<boolean> => {
    const hasErros = Object.values(errMsg).some((err) => err !== "");
    if (!hasErros) {
      try {
        let result: any;

        if (!userFormData?.username) {
          setErrMsg({ ...errMsg, username: " User Name is required!" });
          return false;
        }

        if (isCreateUser) {
          if (userFormData?.profileId === 0) {
            setErrMsg({ ...errMsg, profileId: "Invalid Profile. Please Select Profile" });
            return false;
          }        
          if (!userFormData?.password) {
            setErrMsg({ ...errMsg, password: "Password is required" });
            return false;
          }
          if (userFormData?.password !== userFormData?.confirmPassword) {
            setErrMsg({ ...errMsg, confirmPassword: "Passwords do not match" });
            return false;
          }
          result = await createUser(userPayload(userFormData, passNeverExpire));
        } else {
          if (userFormData?.password !== userFormData?.confirmPassword) {
            setErrMsg({ ...errMsg, confirmPassword: "Passwords do not match" });
            return false;
          }
          result = await updateUser(userPayload(userFormData, passNeverExpire));
        }
        if (result?.error) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The data has been ${isCreateUser ? "added" : "updated"} for ${titleCase(userFormData.username)}.`,
            })
          );
          refetch();
          dispatch(setIsCreateUser(false));
          setSelectedIndex(0);
          if(loggedInUserName == userFormData?.username){
            setTimeout(() => {
                    dispatch(logout());
                    navigate("/login");
                }, 1000);            
          }
          return true;
      }
      } catch (error: any) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
        return false;
      }
    }
    return false;
  };

  const cancelHandler = () => {
      setErrMsg({});
      dispatch(setErrorMsg({...errorMsg, ...clearInputList })); 
      setSelectedIndex(0);    
      setUserFormData({ ...data[0], profileId: data[0]?.profile?.id || 0 });
      setPassNeverExpire(data[0]?.passExpireDate !== 0 ? false : true);
      if (!!data[0]?.passExpireDate) {
        setPassExpireDate(dayjs(data[0]?.passExpireDate));
      }
  };
  const deleteHandler = () => {
    dispatch(setConfirmDialog(DIALOGOPENFOR.USER));
  };

  const deleteUserById = async (item: any) => {
    try {
      const response: any = await deleteUser(item.value);
      if (response?.error) throw response.error;
      if (response?.data?.statusCodeValue === 200) {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `User ${titleCase(item.text)} has been deleted Successfully`,
          })
        );
        refetch();
        setSelectedIndex(0);
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    } finally {
      refetch();
    }
  };

  const handleClickAction = async (actionName: string) => {
    if (!actionName) return;

    try {
      let response: any;
      if (actionName.match("delete")) {
        response = await deleteUser(userFormData?.id);
        if (response?.error) throw response.error;
        if (response?.data?.statusCodeValue === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `User ${titleCase(userFormData?.username!)} has been deleted Successfully`,
            })
          );
          refetch();
          setSelectedIndex(0);
        }
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }finally {
      refetch();
    }
  };
  const onBlurHandler = (event: any) => {
    const { name, value } = event?.target;
    let isUserExist: boolean = false;
    if (name === "username") {
      if (!!value && isCreateUser) {
        isUserExist = data.some((item: any) => item.username.trim().toLowerCase() === value.trim().toLowerCase());
        isUserExist && setErrMsg({ [name]: `User Name Already Exist!` });
      } else if (!!value && !isCreateUser) {
        setErrMsg({ ...errMsg, [name]: `` });
      } else if (!!!value) {
        setErrMsg({ ...errMsg, [name]: `User Name is required!` });
      }
      if (isSpecialChars(value)) {
        setErrMsg({ username: "Special characters are not permitted in this field." });
        return;
      }
    }    
    if (name === "phone") {
      if (!!value && !validatePhoneNumber(value)) {
        setErrMsg({ ...errMsg, [name]: `Invalid Phone number. Please enter a valid country code (optional) and a 10-digit number` });
      } else {
        setErrMsg({ ...errMsg, [name]: `` });
      }
    }
    if (name === "email") {
      if (!!value && isValidEmail(value) === null) {
        setErrMsg({ ...errMsg, [name]: `Invalid Email. example: abc@gmail.com` });
      } else {
        setErrMsg({ ...errMsg, [name]: `` });
      }
    }
  };

  return {
    data,
    isLoading,
    errMsg,
    setErrMsg,
    userFormData,
    setUserFormData,
    selectedIndex,
    setSelectedIndex,
    passExpireDate,
    setPassExpireDate,
    passNeverExpire,
    setPassNeverExpire,
    isChangePass,
    setIsChangePass,
    submitHandler,
    isLoadingStatus,
    deleteHandler,
    deleteUserById,
    handleClickAction,
    isCreateUser,
    onBlurHandler,
    profiles,
    cancelHandler,
    username,
    password,
    confirmPassword,
    passwordExpiryDate,
    firstName,
    lastName,
    department,
    location,
    phone,
    email,
    clearInputList
  };
};

export const useUserDisplay = () => {
  const { data = [], isLoading, refetch } = useGetUserQuery();
  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    data,
    isLoading,
  };
};
